import { useEffect, useMemo, useState } from "react";
import { Backdrop, Button, Checkbox, CircularProgress, FormControlLabel, TextField, Typography } from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import Diversity3OutlinedIcon from "@mui/icons-material/Diversity3Outlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import * as ExcelJS from "exceljs";
import dayjs from "dayjs";
import { saveAs } from "file-saver";

import { findAllModalidadesAcao, findAllTemas, findAllTipologias } from "API/apiAcoes";
import {
  findAllAreasAtuacao,
  findAllFuncoesDesempenhadas,
  findAllLotacoes,
  findAllOrgaosAtuacao,
  findAllVinculosEmpregaticios,
} from "API/apiDadosComplementares";
import {
  findRelatorioParticipacoesPorMunicipio,
  findRelatorioTotalParticipacoes,
  findRelatorioTotalParticipacoesCount,
  RelatorioTotalParticipacoesParams,
} from "API/apiRelatoriosTurma";
import MultiSelectCheck from "components/atoms/MultiSelectField/MultSelectCheck";
import { MultiSelectOption } from "components/atoms/MultiSelectField/MultiSelectOptions";
import SelectField from "components/atoms/SelectField/SelectField";
import { useAppFeedback } from "hooks/useAppFeedback";
import { useLocalidadeStore } from "store/useLocalidadeStore";
import stylesButton from "styles/Button.module.css";
import stylesInput from "styles/Input.module.css";
import { convertListSelect } from "utils/Arrays";
import { formatarCpf } from "utils/Masks";
import { getReportPeriodError } from "utils/reportPeriod";
import { addGovernmentReportLogo, prependGovernmentReportHeader } from "utils/reportExportHeader";

import styles from "../Relatorios.module.css";

type RelatorioTotalParticipacoesProps = {
  selectedReportLabel: string;
};

type SelectValue = Array<number | string>;

type ParticipacaoDetalhadaItem = {
  municipio_nome?: string | null;
  regional_sedese_municipio?: string[];
  usuario?: {
    nome?: string | null;
    cpf?: string | null;
    email?: string | null;
  };
  dados_pessoais?: {
    escolaridade?: string | null;
    data_nascimento?: string | null;
    idade?: number | null;
    identidade_genero?: string | null;
    identidade_genero_outros?: string | null;
    cor_raca?: string | null;
    possui_deficiencia?: boolean | null;
  };
  dados_profissionais?: {
    ambito_id?: number | null;
    ambito_nome?: string | null;
    lotacao_nome?: string | null;
    orgao_nome?: string | null;
    areas_atuacao?: Array<{
      area_nome?: string | null;
      label?: string | null;
    }>;
    funcao_nome?: string | null;
    vinculo_nome?: string | null;
  };
  acao?: {
    tipologia_nome?: string | null;
    percurso_formativo?: string | null;
    modalidade_nome?: string | null;
    temas?: Array<{
      nome?: string | null;
      label?: string | null;
    }>;
  };
  total_participacoes?: number | null;
  total_participacoes_dados_profissionais?: number | null;
};

type ParticipacaoMunicipioItem = {
  municipio_nome?: string | null;
  regional_sedese_municipio?: string[];
  totais?: {
    lotacoes?: Array<{
      id?: number | null;
      nome?: string | null;
      label?: string | null;
      total?: number | null;
    }>;
    orgaos_atuacao?: Array<{
      id?: number | null;
      nome?: string | null;
      label?: string | null;
      total?: number | null;
    }>;
  };
  total_participacoes?: number | null;
};

const COR_RACA_OPTIONS: MultiSelectOption[] = [
  { id: "Branca", nome: "Branca" },
  { id: "Preta", nome: "Preta" },
  { id: "Amarela", nome: "Amarela" },
  { id: "Parda", nome: "Parda" },
];

const AMBITO_OPTIONS = [
  { id: 1, nome: "Estadual" },
  { id: 2, nome: "Municipal" },
];

const DEFICIENCIA_OPTIONS = [{ nome: "Sim" }, { nome: "Não" }];

const AGE_OPTIONS: MultiSelectOption[] = Array.from({ length: 62 }, (_, index) => {
  const age = index + 14;
  return { id: age, nome: String(age) };
});

const formatDate = (value?: string | null) => {
  if (!value) return "";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : "";
};

const joinText = (values: Array<string | null | undefined>) =>
  values.map((value) => value?.trim()).filter(Boolean).join(", ");

const calculateAge = (date?: string | null, fallback?: number | null) => {
  if (date) {
    const parsed = dayjs(date);
    if (parsed.isValid()) {
      return dayjs().diff(parsed, "year");
    }
  }
  return fallback ?? "";
};

const buildHeader = (worksheet: ExcelJS.Worksheet, columnsLength: number, headerRowNumber = 1) => {
  const headerRow = worksheet.getRow(headerRowNumber);
  headerRow.height = 22;
  headerRow.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF004D40" },
    };
    cell.font = {
      bold: true,
      color: { argb: "FFFFFFFF" },
    };
    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true,
    };
  });

  worksheet.autoFilter = {
    from: { row: headerRowNumber, column: 1 },
    to: { row: headerRowNumber, column: columnsLength },
  };
};

const RelatorioTotalParticipacoes = ({ selectedReportLabel }: RelatorioTotalParticipacoesProps) => {
  const { showError, showSuccess } = useAppFeedback();
  const ensureEstados = useLocalidadeStore((state) => state.ensureEstados);
  const ensureCidades = useLocalidadeStore((state) => state.ensureCidades);

  const [dataInicial, setDataInicial] = useState("");
  const [dataFinal, setDataFinal] = useState("");
  const periodError = getReportPeriodError(dataInicial, dataFinal);
  const periodValidationError = dataInicial && dataFinal ? periodError : null;
  const [todosMunicipiosEstado, setTodosMunicipiosEstado] = useState(false);
  const [municipiosSelecionados, setMunicipiosSelecionados] = useState<SelectValue>([]);
  const [ambitosSelecionados, setAmbitosSelecionados] = useState<SelectValue>([]);
  const [lotacoesSelecionadas, setLotacoesSelecionadas] = useState<SelectValue>([]);
  const [orgaosSelecionados, setOrgaosSelecionados] = useState<SelectValue>([]);
  const [areasSelecionadas, setAreasSelecionadas] = useState<SelectValue>([]);
  const [funcoesSelecionadas, setFuncoesSelecionadas] = useState<SelectValue>([]);
  const [vinculosSelecionados, setVinculosSelecionados] = useState<SelectValue>([]);
  const [tipologiasSelecionadas, setTipologiasSelecionadas] = useState<SelectValue>([]);
  const [temasSelecionados, setTemasSelecionados] = useState<SelectValue>([]);
  const [modalidadesSelecionadas, setModalidadesSelecionadas] = useState<SelectValue>([]);
  const [idadesSelecionadas, setIdadesSelecionadas] = useState<SelectValue>([]);
  const [coresRacasSelecionadas, setCoresRacasSelecionadas] = useState<SelectValue>([]);
  const [possuiDeficiencia, setPossuiDeficiencia] = useState("");

  const [municipiosOptions, setMunicipiosOptions] = useState<MultiSelectOption[]>([]);
  const [lotacoesOptions, setLotacoesOptions] = useState<MultiSelectOption[]>([]);
  const [orgaosOptions, setOrgaosOptions] = useState<MultiSelectOption[]>([]);
  const [areasOptions, setAreasOptions] = useState<MultiSelectOption[]>([]);
  const [funcoesOptions, setFuncoesOptions] = useState<MultiSelectOption[]>([]);
  const [vinculosOptions, setVinculosOptions] = useState<MultiSelectOption[]>([]);
  const [tipologiasOptions, setTipologiasOptions] = useState<MultiSelectOption[]>([]);
  const [temasOptions, setTemasOptions] = useState<MultiSelectOption[]>([]);
  const [modalidadesOptions, setModalidadesOptions] = useState<MultiSelectOption[]>([]);

  const [loadingOptions, setLoadingOptions] = useState(false);
  const [loadingCount, setLoadingCount] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [estimatedCount, setEstimatedCount] = useState<number | null>(null);

  const possuiAmbitoEstadual = ambitosSelecionados.includes(1);
  const possuiAmbitoMunicipal = ambitosSelecionados.includes(2);

  const selectedMunicipioIds = useMemo(
    () => municipiosSelecionados.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [municipiosSelecionados],
  );
  const selectedAmbitosIds = useMemo(
    () => ambitosSelecionados.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [ambitosSelecionados],
  );
  const selectedLotacoesIds = useMemo(
    () => lotacoesSelecionadas.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [lotacoesSelecionadas],
  );
  const selectedOrgaosIds = useMemo(
    () => orgaosSelecionados.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [orgaosSelecionados],
  );
  const selectedAreasIds = useMemo(
    () => areasSelecionadas.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [areasSelecionadas],
  );
  const selectedFuncoesIds = useMemo(
    () => funcoesSelecionadas.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [funcoesSelecionadas],
  );
  const selectedVinculosIds = useMemo(
    () => vinculosSelecionados.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [vinculosSelecionados],
  );
  const selectedTipologiasIds = useMemo(
    () => tipologiasSelecionadas.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [tipologiasSelecionadas],
  );
  const selectedTemasIds = useMemo(
    () => temasSelecionados.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [temasSelecionados],
  );
  const selectedModalidadesIds = useMemo(
    () => modalidadesSelecionadas.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [modalidadesSelecionadas],
  );
  const selectedIdades = useMemo(
    () => idadesSelecionadas.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [idadesSelecionadas],
  );
  const selectedCoresRacas = useMemo(
    () => coresRacasSelecionadas.map((item) => String(item)),
    [coresRacasSelecionadas],
  );

  const hasRequiredFiltersForCount = useMemo(
    () =>
      Boolean(
        !periodError &&
          (todosMunicipiosEstado || selectedMunicipioIds.length > 0) &&
          selectedAmbitosIds.length > 0,
      ),
    [periodError, selectedAmbitosIds.length, selectedMunicipioIds.length, todosMunicipiosEstado],
  );

  useEffect(() => {
    const loadOptions = async () => {
      try {
        setLoadingOptions(true);
        const estados = await ensureEstados();
        const estadoMG = estados.find((item) => item.sigla === "MG");
        if (!estadoMG) {
          throw new Error("Estado MG nao encontrado.");
        }

        const [municipiosMg, lotacoes, orgaos, areas, funcoes, vinculos, tipologias, temas, modalidades] =
          await Promise.all([
            ensureCidades({ estado_id: estadoMG.estado_id }),
            findAllLotacoes(),
            findAllOrgaosAtuacao(),
            findAllAreasAtuacao(),
            findAllFuncoesDesempenhadas(),
            findAllVinculosEmpregaticios(),
            findAllTipologias(),
            findAllTemas(),
            findAllModalidadesAcao(),
          ]);

        setMunicipiosOptions(
          convertListSelect(
            [...municipiosMg].sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR")),
            "id",
            "nome",
          ),
        );
        setLotacoesOptions(convertListSelect(lotacoes, "id", "nome"));
        setOrgaosOptions(convertListSelect(orgaos, "id", "nome"));
        setAreasOptions(convertListSelect(areas, "id", "nome"));
        setFuncoesOptions(convertListSelect(funcoes, "id", "nome"));
        setVinculosOptions(convertListSelect(vinculos, "id", "nome"));
        setTipologiasOptions(convertListSelect(tipologias, "id", "nome"));
        setTemasOptions(convertListSelect(temas, "id", "nome"));
        setModalidadesOptions(convertListSelect(modalidades, "id", "nome"));
      } catch (error) {
        showError("Nao foi possivel carregar os filtros do relatorio.", error);
      } finally {
        setLoadingOptions(false);
      }
    };

    void loadOptions();
  }, [ensureCidades, ensureEstados, showError]);

  useEffect(() => {
    if (!possuiAmbitoEstadual) {
      setLotacoesSelecionadas([]);
    }
    if (!possuiAmbitoMunicipal) {
      setOrgaosSelecionados([]);
    }
  }, [possuiAmbitoEstadual, possuiAmbitoMunicipal]);

  const buildParams = (): RelatorioTotalParticipacoesParams | null => {
    if (periodError) {
      return null;
    }

    return {
      data_inicio: dataInicial,
      data_fim: dataFinal,
      todos_municipios_estado: todosMunicipiosEstado || undefined,
      municipios_ids: !todosMunicipiosEstado && selectedMunicipioIds.length > 0 ? selectedMunicipioIds : undefined,
      ambitos_ids: selectedAmbitosIds.length > 0 ? selectedAmbitosIds : undefined,
      lotacoes_ids: possuiAmbitoEstadual && selectedLotacoesIds.length > 0 ? selectedLotacoesIds : undefined,
      orgaos_ids: possuiAmbitoMunicipal && selectedOrgaosIds.length > 0 ? selectedOrgaosIds : undefined,
      areas_atuacao_ids: selectedAreasIds.length > 0 ? selectedAreasIds : undefined,
      funcoes_ids: selectedFuncoesIds.length > 0 ? selectedFuncoesIds : undefined,
      vinculos_ids: selectedVinculosIds.length > 0 ? selectedVinculosIds : undefined,
      tipologias_ids: selectedTipologiasIds.length > 0 ? selectedTipologiasIds : undefined,
      temas_ids: selectedTemasIds.length > 0 ? selectedTemasIds : undefined,
      modalidades_ids: selectedModalidadesIds.length > 0 ? selectedModalidadesIds : undefined,
      idades: selectedIdades.length > 0 ? selectedIdades : undefined,
      cores_racas: selectedCoresRacas.length > 0 ? selectedCoresRacas : undefined,
      possui_deficiencia:
        possuiDeficiencia === "Sim" ? true : possuiDeficiencia === "Não" ? false : undefined,
    };
  };

  useEffect(() => {
    if (!hasRequiredFiltersForCount) {
      setEstimatedCount(null);
      return;
    }

    const params = buildParams();
    if (!params) {
      setEstimatedCount(null);
      return;
    }

    const loadCount = async () => {
      try {
        setLoadingCount(true);
        const response = await findRelatorioTotalParticipacoesCount(params);
        setEstimatedCount(response.count ?? 0);
      } catch (error) {
        setEstimatedCount(null);
        showError("Nao foi possivel calcular o resultado estimado do relatorio.", error);
      } finally {
        setLoadingCount(false);
      }
    };

    void loadCount();
  }, [
    dataInicial,
    dataFinal,
    todosMunicipiosEstado,
    selectedMunicipioIds,
    selectedAmbitosIds,
    possuiAmbitoEstadual,
    possuiAmbitoMunicipal,
    selectedLotacoesIds,
    selectedOrgaosIds,
    selectedAreasIds,
    selectedVinculosIds,
    selectedTipologiasIds,
    selectedTemasIds,
    selectedModalidadesIds,
    selectedIdades,
    selectedCoresRacas,
    possuiDeficiencia,
    hasRequiredFiltersForCount,
    showError,
  ]);

  const handleClear = () => {
    setDataInicial("");
    setDataFinal("");
    setTodosMunicipiosEstado(false);
    setMunicipiosSelecionados([]);
    setAmbitosSelecionados([]);
    setLotacoesSelecionadas([]);
    setOrgaosSelecionados([]);
    setAreasSelecionadas([]);
    setFuncoesSelecionadas([]);
    setVinculosSelecionados([]);
    setTipologiasSelecionadas([]);
    setTemasSelecionados([]);
    setModalidadesSelecionadas([]);
    setIdadesSelecionadas([]);
    setCoresRacasSelecionadas([]);
    setPossuiDeficiencia("");
    setEstimatedCount(null);
  };

  const buildWorkbook = (dadosAba1: ParticipacaoDetalhadaItem[], dadosAba2: ParticipacaoMunicipioItem[]) => {
    const workbook = new ExcelJS.Workbook();

    const aba1Headers = [
      "MUNICÍPIO",
      "REGIONAL SEDESE DO MUNICÍPIO",
      "NOME",
      "CPF",
      "E-MAIL",
      "ÂMBITO DE ATUAÇÃO",
      "ÓRGÃO DE ATUAÇÃO OU LOTAÇÃO",
      "ÁREA DE ATUAÇÃO",
      "FUNÇÃO DESEMPENHADA",
      "VÍNCULO EMPREGATÍCIO",
      "TIPOLOGIA",
      "PERCURSO FORMATIVO",
      "TEMA",
      "MODALIDADE DA AÇÃO",
      "ESCOLARIDADE",
      "IDADE (ATRAVÉS DA DATA DE NASCIMENTO)",
      "IDENTIDADE DE GÊNERO",
      "COR/RAÇA",
      "PESSOA COM DEFICIÊNCIA",
      "TOTAL DE PARTICIPAÇÕES",
    ];

    const aba1 = workbook.addWorksheet("Perfil Demografico", {
      views: [{ state: "frozen", xSplit: 1, ySplit: 5 }],
    });

    aba1.columns = aba1Headers.map((header, index) => ({
      header,
      key: `col_${index}`,
      width: index === 0 ? 24 : Math.max(18, Math.min(40, header.length + 4)),
    }));
    const aba1HeaderRowNumber = prependGovernmentReportHeader(aba1, aba1Headers.length) + 1;
    buildHeader(aba1, aba1Headers.length, aba1HeaderRowNumber);

    dadosAba1.forEach((item) => {
      aba1.addRow([
        item.municipio_nome || "",
        joinText(item.regional_sedese_municipio || []),
        item.usuario?.nome || "",
        formatarCpf(item.usuario?.cpf || ""),
        item.usuario?.email || "",
        item.dados_profissionais?.ambito_nome || "",
        item.dados_profissionais?.ambito_id === 1
          ? item.dados_profissionais?.lotacao_nome || ""
          : item.dados_profissionais?.orgao_nome || "",
        joinText((item.dados_profissionais?.areas_atuacao || []).map((area) => area.area_nome || area.label)),
        item.dados_profissionais?.funcao_nome || "",
        item.dados_profissionais?.vinculo_nome || "",
        item.acao?.tipologia_nome || "",
        item.acao?.percurso_formativo || "",
        joinText((item.acao?.temas || []).map((tema) => tema.nome || tema.label)),
        item.acao?.modalidade_nome || "",
        item.dados_pessoais?.escolaridade || "",
        calculateAge(item.dados_pessoais?.data_nascimento, item.dados_pessoais?.idade),
        item.dados_pessoais?.identidade_genero || "",
        item.dados_pessoais?.cor_raca || "",
        item.dados_pessoais?.possui_deficiencia ? "Sim" : "Não",
        item.total_participacoes ?? 0,
      ]);
    });

    const firstMunicipioResumo = dadosAba2[0];
    const lotacoesDinamicas = firstMunicipioResumo?.totais?.lotacoes || [];
    const orgaosDinamicos = firstMunicipioResumo?.totais?.orgaos_atuacao || [];
    const aba2Headers = [
      "MUNICIPIO",
      "REGIONAL SEDESE DO MUNICÍPIO",
      ...lotacoesDinamicas.map((item) => item.nome || item.label || ""),
      ...orgaosDinamicos.map((item) => item.nome || item.label || ""),
      "TOTAL DE PARTICIPAÇÕES",
    ];

    const aba2 = workbook.addWorksheet("Relatorio Simplificado", {
      views: [{ state: "frozen", xSplit: 1, ySplit: 5 }],
    });

    aba2.columns = aba2Headers.map((header, index) => ({
      header,
      key: `col_${index}`,
      width: index === 0 ? 24 : Math.max(18, Math.min(38, header.length + 4)),
    }));
    const aba2HeaderRowNumber = prependGovernmentReportHeader(aba2, aba2Headers.length) + 1;
    buildHeader(aba2, aba2Headers.length, aba2HeaderRowNumber);

    dadosAba2.forEach((item) => {
      const lotacaoMap = new Map((item.totais?.lotacoes || []).map((lotacao) => [lotacao.id, lotacao.total ?? 0]));
      const orgaoMap = new Map((item.totais?.orgaos_atuacao || []).map((orgao) => [orgao.id, orgao.total ?? 0]));

      aba2.addRow([
        item.municipio_nome || "",
        joinText(item.regional_sedese_municipio || []),
        ...lotacoesDinamicas.map((lotacao) => lotacaoMap.get(lotacao.id) ?? 0),
        ...orgaosDinamicos.map((orgao) => orgaoMap.get(orgao.id) ?? 0),
        item.total_participacoes ?? 0,
      ]);
    });

    return workbook;
  };

  const handleGenerateReport = async () => {
    if (periodError) {
      showError(periodError);
      return;
    }

    const params = buildParams();
    if (!params) {
      showError("Informe a data inicial e a data final para gerar o relatorio.");
      return;
    }

    try {
      setGeneratingReport(true);
      const [responseAba1, responseAba2] = await Promise.all([
        findRelatorioTotalParticipacoes(params),
        findRelatorioParticipacoesPorMunicipio(params),
      ]);

      const workbook = buildWorkbook(
        (responseAba1.data || []) as ParticipacaoDetalhadaItem[],
        (responseAba2.data || []) as ParticipacaoMunicipioItem[],
      );
      await Promise.all(workbook.worksheets.map((worksheet) => addGovernmentReportLogo(workbook, worksheet)));
      const excelBuffer = await workbook.xlsx.writeBuffer();

      saveAs(
        new Blob([excelBuffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        "relatorio_total_participacoes.xlsx",
      );

      showSuccess("Relatorio gerado com sucesso.");
    } catch (error) {
      showError("Nao foi possivel gerar o relatorio.", error);
    } finally {
      setGeneratingReport(false);
    }
  };

  return (
    <>
      <div className={styles.reportFilterBody}>
        <div className={styles.reportHeaderRow}>
          <div className={styles.reportSelectedWrap}>
            <span className={styles.reportSelectedLabel}>Relatório selecionado:</span>
            <span className={styles.reportSelectedChip}>{selectedReportLabel}</span>
          </div>

          <div className={styles.exportFormatCard}>
            <div className={styles.exportFormatLabel}>Formato de exportação</div>
            <div className={styles.exportFormatValue}>
              <TableChartOutlinedIcon className={styles.exportFormatIcon} />
              XLSX
            </div>
          </div>
        </div>

        <div className={styles.reportDivider}></div>

        <div className={styles.filterSection}>
          <div className={styles.filterBlockTitle}>
            Período <span className={styles.optionalText}>(obrigatório)</span>
          </div>
          <div className={styles.filterGridTwo}>
            <div>
              <label className={stylesInput.titleInputSoft}>Data inicial</label>
              <TextField
                type="date"
                fullWidth
                size="small"
                variant="standard"
                value={dataInicial}
                error={Boolean(periodValidationError)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDataInicial(event.target.value)}
                InputProps={{ className: stylesInput.inputSoft, disableUnderline: true }}
              />
            </div>
            <div>
              <label className={stylesInput.titleInputSoft}>Data final</label>
              <TextField
                type="date"
                fullWidth
                size="small"
                variant="standard"
                value={dataFinal}
                error={Boolean(periodValidationError)}
                helperText={periodValidationError || undefined}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDataFinal(event.target.value)}
                InputProps={{ className: stylesInput.inputSoft, disableUnderline: true }}
              />
            </div>
          </div>
        </div>

        <div className={styles.filterSection}>
          <FormControlLabel
            control={
              <Checkbox
                checked={todosMunicipiosEstado}
                onChange={(event) => {
                  setTodosMunicipiosEstado(event.target.checked);
                  if (event.target.checked) {
                    setMunicipiosSelecionados([]);
                  }
                }}
              />
            }
            label="Todos municipios do estado"
          />
        </div>

        <div className={styles.filterSection}>
          <div className={styles.filterBlockTitle}>
            Municípios <span className={styles.optionalText}>(obrigatório)</span>
          </div>
          <MultiSelectCheck
            label=""
            placeholder="Selecione um ou mais municípios"
            fullWidth
            value={municipiosSelecionados}
            onChange={setMunicipiosSelecionados}
            options={municipiosOptions}
            disabled={todosMunicipiosEstado || loadingOptions}
            selectedDisplayMode="count"
            paperClassname={stylesInput.paper}
            popperClassname={stylesInput.popper}
            titleInputClassname={styles.reportHiddenLabel}
            inputSelectClassname={stylesInput.inputSoft}
            labelSelectClassname={stylesInput.labelSelect}
          />
        </div>

        <div className={styles.filterSection}>
          <div className={styles.filterBlockTitle}>
            Âmbitos <span className={styles.optionalText}>(obrigatório)</span>
          </div>
          <div className={styles.reportCheckboxGroup}>
            {AMBITO_OPTIONS.map((item) => (
              <FormControlLabel
                key={item.id}
                control={
                  <Checkbox
                    checked={ambitosSelecionados.includes(item.id)}
                    onChange={(event) => {
                      setAmbitosSelecionados((current) =>
                        event.target.checked
                          ? [...current, item.id]
                          : current.filter((value) => Number(value) !== item.id),
                      );
                    }}
                  />
                }
                label={item.nome}
              />
            ))}
          </div>
        </div>

        <div className={styles.filterGridTwo}>
          {possuiAmbitoEstadual && (
            <div className={styles.filterSection}>
              <div className={styles.filterBlockTitle}>
                Lotações <span className={styles.optionalText}>(obrigatório)</span>
              </div>
              <MultiSelectCheck
                label=""
                placeholder="Selecione uma ou mais lotações"
                fullWidth
                value={lotacoesSelecionadas}
                onChange={setLotacoesSelecionadas}
                options={lotacoesOptions}
                disabled={loadingOptions}
                selectedDisplayMode="count"
                paperClassname={stylesInput.paper}
                popperClassname={stylesInput.popper}
                titleInputClassname={styles.reportHiddenLabel}
                inputSelectClassname={stylesInput.inputSoft}
                labelSelectClassname={stylesInput.labelSelect}
              />
            </div>
          )}

          {possuiAmbitoMunicipal && (
            <div className={styles.filterSection}>
              <div className={styles.filterBlockTitle}>
                Órgãos de atuação <span className={styles.optionalText}>(obrigatório)</span>
              </div>
              <MultiSelectCheck
                label=""
                placeholder="Selecione um ou mais órgãos"
                fullWidth
                value={orgaosSelecionados}
                onChange={setOrgaosSelecionados}
                options={orgaosOptions}
                disabled={loadingOptions}
                selectedDisplayMode="count"
                paperClassname={stylesInput.paper}
                popperClassname={stylesInput.popper}
                titleInputClassname={styles.reportHiddenLabel}
                inputSelectClassname={stylesInput.inputSoft}
                labelSelectClassname={stylesInput.labelSelect}
              />
            </div>
          )}
        </div>

        <div className={styles.filterGridTwo}>
          <div className={styles.filterSection}>
            <div className={styles.filterBlockTitle}>Áreas de atuação</div>
            <MultiSelectCheck
              label=""
              placeholder="Selecione uma ou mais áreas"
              fullWidth
              value={areasSelecionadas}
              onChange={setAreasSelecionadas}
              options={areasOptions}
              disabled={loadingOptions}
              selectedDisplayMode="count"
              paperClassname={stylesInput.paper}
              popperClassname={stylesInput.popper}
              titleInputClassname={styles.reportHiddenLabel}
              inputSelectClassname={stylesInput.inputSoft}
              labelSelectClassname={stylesInput.labelSelect}
            />
          </div>
          <div className={styles.filterSection}>
            <div className={styles.filterBlockTitle}>Funções desempenhadas</div>
            <MultiSelectCheck
              label=""
              placeholder="Selecione uma ou mais funções"
              fullWidth
              value={funcoesSelecionadas}
              onChange={setFuncoesSelecionadas}
              options={funcoesOptions}
              disabled={loadingOptions}
              selectedDisplayMode="count"
              paperClassname={stylesInput.paper}
              popperClassname={stylesInput.popper}
              titleInputClassname={styles.reportHiddenLabel}
              inputSelectClassname={stylesInput.inputSoft}
              labelSelectClassname={stylesInput.labelSelect}
            />
          </div>
        </div>

        <div className={styles.filterGridTwo}>
          <div className={styles.filterSection}>
            <div className={styles.filterBlockTitle}>Vínculos empregatícios</div>
            <MultiSelectCheck
              label=""
              placeholder="Selecione um ou mais vínculos"
              fullWidth
              value={vinculosSelecionados}
              onChange={setVinculosSelecionados}
              options={vinculosOptions}
              disabled={loadingOptions}
              selectedDisplayMode="count"
              paperClassname={stylesInput.paper}
              popperClassname={stylesInput.popper}
              titleInputClassname={styles.reportHiddenLabel}
              inputSelectClassname={stylesInput.inputSoft}
              labelSelectClassname={stylesInput.labelSelect}
            />
          </div>
          <div className={styles.filterSection}>
            <div className={styles.filterBlockTitle}>Modalidades</div>
            <MultiSelectCheck
              label=""
              placeholder="Selecione uma ou mais modalidades"
              fullWidth
              value={modalidadesSelecionadas}
              onChange={setModalidadesSelecionadas}
              options={modalidadesOptions}
              disabled={loadingOptions}
              selectedDisplayMode="count"
              paperClassname={stylesInput.paper}
              popperClassname={stylesInput.popper}
              titleInputClassname={styles.reportHiddenLabel}
              inputSelectClassname={stylesInput.inputSoft}
              labelSelectClassname={stylesInput.labelSelect}
            />
          </div>
        </div>

          <div className={styles.filterSection}>
            <div className={styles.filterBlockTitle}>Tipologias</div>
            <MultiSelectCheck
              label=""
              placeholder="Selecione uma ou mais tipologias"
              fullWidth
              value={tipologiasSelecionadas}
              onChange={setTipologiasSelecionadas}
              options={tipologiasOptions}
              disabled={loadingOptions}
              selectedDisplayMode="count"
              paperClassname={stylesInput.paper}
              popperClassname={stylesInput.popper}
              titleInputClassname={styles.reportHiddenLabel}
              inputSelectClassname={stylesInput.inputSoft}
              labelSelectClassname={stylesInput.labelSelect}
            />
          </div>
          <div className={styles.filterSection}>
            <div className={styles.filterBlockTitle}>Temas</div>
            <MultiSelectCheck
              label=""
              placeholder="Selecione um ou mais temas"
              fullWidth
              value={temasSelecionados}
              onChange={setTemasSelecionados}
              options={temasOptions}
              disabled={loadingOptions}
              selectedDisplayMode="count"
              paperClassname={stylesInput.paper}
              popperClassname={stylesInput.popper}
              titleInputClassname={styles.reportHiddenLabel}
              inputSelectClassname={stylesInput.inputSoft}
              labelSelectClassname={stylesInput.labelSelect}
            />
          </div>
          

        <div className={styles.filterGridTwo}>
          <div className={styles.filterSection}>
            <div className={styles.filterBlockTitle}>Idades</div>
            <MultiSelectCheck
              label=""
              placeholder="Selecione uma ou mais idades"
              fullWidth
              value={idadesSelecionadas}
              onChange={setIdadesSelecionadas}
              options={AGE_OPTIONS}
              disabled={loadingOptions}
              selectedDisplayMode="count"
              paperClassname={stylesInput.paper}
              popperClassname={stylesInput.popper}
              titleInputClassname={styles.reportHiddenLabel}
              inputSelectClassname={stylesInput.inputSoft}
              labelSelectClassname={stylesInput.labelSelect}
            />
          </div>
          <div className={styles.filterSection}>
            <div className={styles.filterBlockTitle}>Cor/Raça</div>
            <MultiSelectCheck
              label=""
              placeholder="Selecione uma ou mais opções"
              fullWidth
              value={coresRacasSelecionadas}
              onChange={setCoresRacasSelecionadas}
              options={COR_RACA_OPTIONS}
              disabled={loadingOptions}
              selectedDisplayMode="count"
              paperClassname={stylesInput.paper}
              popperClassname={stylesInput.popper}
              titleInputClassname={styles.reportHiddenLabel}
              inputSelectClassname={stylesInput.inputSoft}
              labelSelectClassname={stylesInput.labelSelect}
            />
          </div>
          <div className={styles.filterSection}>
            <div className={styles.filterBlockTitle}>Pessoas com deficiência</div>
            <SelectField
              label=""
              placeholder="Selecione"
              value={possuiDeficiencia}
              onChange={setPossuiDeficiencia}
              options={DEFICIENCIA_OPTIONS}
              disableTyping
              inputClassName={stylesInput.inputSoft}
              containerClassName={styles.reportSelectContainer}
            />
          </div>
        </div>

        <div className={styles.reportInfoBanner}>
          <InfoOutlinedIcon className={styles.reportInfoBannerIcon} />
          <span>
            A exportação será gerada com duas abas: perfil demográfico e relatório simplificado por município.
          </span>
        </div>

        <div className={styles.selectionSummaryCard}>
          <div className={styles.selectionSummaryTitle}>Resumo da seleção</div>
          <div className={styles.selectionSummaryGrid}>
            <div className={styles.selectionSummaryItem}>
              <CalendarMonthOutlinedIcon className={styles.selectionSummaryIcon} />
              <div>
                <div className={styles.selectionSummaryLabel}>Período</div>
                <div className={styles.selectionSummaryValue}>
                  {formatDate(dataInicial)} a {formatDate(dataFinal)}
                </div>
              </div>
            </div>
            <div className={styles.selectionSummaryItem}>
              <PlaceOutlinedIcon className={styles.selectionSummaryIcon} />
              <div>
                <div className={styles.selectionSummaryLabel}>Municípios</div>
                <div className={styles.selectionSummaryValue}>
                  {todosMunicipiosEstado ? "Todos do estado" : `${selectedMunicipioIds.length} selecionado(s)`}
                </div>
              </div>
            </div>
            <div className={styles.selectionSummaryItem}>
              <Diversity3OutlinedIcon className={styles.selectionSummaryIcon} />
              <div>
                <div className={styles.selectionSummaryLabel}>Âmbitos</div>
                <div className={styles.selectionSummaryValue}>{selectedAmbitosIds.length} selecionado(s)</div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.reportActionsRow}>
          <Button
            variant="outlined"
            className={stylesButton.buttonSoftMuted}
            startIcon={<DeleteOutlineOutlinedIcon />}
            onClick={handleClear}
          >
            Limpar filtros
          </Button>

          <div className={styles.reportActionsRight}>
            <div className={styles.estimatedResultCard}>
              <div className={styles.estimatedResultLabel}>Resultado estimado</div>
              <div className={styles.estimatedResultValue}>
                {loadingCount ? <CircularProgress size={18} /> : estimatedCount ?? "-"}
              </div>
              <div className={styles.estimatedResultMeta}>registros</div>
            </div>

            <div className={styles.generateButtonGroup}>
              <Button
                variant="contained"
                className={styles.generateButton}
                startIcon={<DownloadOutlinedIcon />}
                onClick={handleGenerateReport}
                disabled={generatingReport || loadingOptions}
              >
                Gerar relatório
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Backdrop
        open={generatingReport}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 100,
          flexDirection: "column",
          gap: 2,
          backgroundColor: "rgba(15, 23, 42, 0.45)",
        }}
      >
        <CircularProgress color="inherit" />
        <Typography sx={{ fontWeight: 600 }}>Gerando relatório...</Typography>
      </Backdrop>
    </>
  );
};

export default RelatorioTotalParticipacoes;
